<template>
  <div class="gamanza-account-dropdown" :class="{ 'gamanza-account-dropdown--mobile': mobilePanel }" @click.stop>
    <gamanza-avatar
      :skin="skin"
      :language="language"
      :show-summary="!compactAvatar"
      external-panel
      @open-panel="openPanel"
    />

    <transition name="gamanza-dropdown-panel">
    <section v-if="isOpen" class="gamanza-account-dropdown__panel" :style="[panelStyle, profileStyle]">
      <button type="button" class="gamanza-account-dropdown__close" aria-label="Cerrar menú" @click="closePanel">
        <i class="fas fa-times" aria-hidden="true"></i>
      </button>

      <div class="gamanza-account-dropdown__identity">
        <span>ID: {{ userId }}</span>
        <strong>{{ userName }}</strong>
      </div>

      <div class="gamanza-account-dropdown__balances">
        <div><span>Saldo:</span><strong>{{ formatAmount(balance) }}</strong></div>
        <div v-if="showStandardBonusBalances"><span>Saldo de bono:</span><strong>{{ formatAmount(bonus) }}</strong></div>
        <div v-if="showStandardBonusBalances"><span>Saldo congelado:</span><strong>{{ formatAmount(frozen) }}</strong></div>
      </div>

      <div v-if="showAccountActions || showLogout" class="gamanza-account-dropdown__account-actions">
        <template v-if="showAccountActions">
          <button type="button" @click="emitAndClose('open-account')"><i class="fas fa-user"></i>Mi cuenta</button>
          <button type="button" @click="emitAndClose('open-balance-history')"><i class="fas fa-file-invoice-dollar"></i>Historial de saldo</button>
          <button type="button" @click="emitAndClose('open-change-password')"><i class="fas fa-key"></i>Cambiar contraseña</button>
        </template>
        <button v-if="showLogout" type="button" class="gamanza-account-dropdown__logout" @click="emitAndClose('logout')"><i class="fas fa-sign-out-alt"></i>Cerrar sesión</button>
      </div>

      <div class="gamanza-account-dropdown__experience">
        <div class="gamanza-account-dropdown__experience-title">
          <span class="gamanza-account-dropdown__mark"><i class="fas fa-trophy" aria-hidden="true"></i></span>
          <span>MI EXPERIENCIA</span>
        </div>
        <div class="gamanza-account-dropdown__experience-stats">
          <div v-for="stat in experienceStatWidgets" :key="stat.key" class="gamanza-account-dropdown__experience-stat"
            :class="`gamanza-account-dropdown__experience-stat--${stat.key}`">
            <span>{{ stat.label }}</span>
            <div :id="stat.id" class="gamification_widget" data-type="gamification-data"
              :data-gamification-type="stat.gamificationType" :data-show-name="stat.showName"
              :data-progress-type="stat.progressType"></div>
          </div>
        </div>
        <button v-if="showMissions" type="button" @click="navigateAndClose('gamanza_missions')"><i class="fas fa-tasks"></i>Misiones<i class="fas fa-chevron-right"></i></button>
        <button v-if="showTournaments" type="button" @click="navigateAndClose('gamanza_tournaments')"><i class="fas fa-trophy"></i>Torneos<i class="fas fa-chevron-right"></i></button>
        <button v-if="showRewardShop" type="button" @click="navigateAndClose('gamanza_reward_shop')"><i class="fas fa-gift"></i>Tienda de recompensas<i class="fas fa-chevron-right"></i></button>
      </div>
    </section>
    </transition>
  </div>
</template>

<script>
import GamanzaAvatar from './GamanzaAvatar.vue';
import gamanzaWidgetService from '@/services/gamanzaWidgetService';

import {
  createGamanzaProfileStatWidgets,
  getGamanzaProfilePalette
} from '../../utils/gamanzaProfileDropdown';

export default {
  name: 'GamanzaAccountDropdown',
  components: {
    GamanzaAvatar
  },
  props: {
    skin: {
      type: Object,
      required: true
    },
    language: {
      type: String,
      default: 'es'
    },
    user: {
      type: Object,
      required: true
    },
    balance: {
      type: Number,
      default: 0
    },
    bonus: {
      type: Number,
      default: 0
    },
    frozen: {
      type: Number,
      default: 0
    },
    showStandardBonusBalances: {
      type: Boolean,
      default: true
    },
    compactAvatar: {
      type: Boolean,
      default: false
    },
    mobilePanel: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isOpen: false,
      experienceStatWidgets: createGamanzaProfileStatWidgets(this._uid)
    };
  },
  computed: {
    userId() {
      return this.user?.user_data?.data?.user_id || '';
    },
    userName() {
      return this.user?.user_data?.data?.user_name || '';
    },
    showAccountActions() {
      return Boolean(this.skin?.gamanza?.mainFrame?.profileDropdown?.accountActions);
    },
    showLogout() {
      return Boolean(this.skin?.gamanza?.mainFrame?.profileDropdown?.logout);
    },
    showMissions() {
      return Boolean(this.skin?.gamanza?.mainFrame?.missions);
    },
    showTournaments() {
      return Boolean(this.skin?.gamanza?.mainFrame?.tournaments);
    },
    showRewardShop() {
      return Boolean(this.skin?.gamanza?.mainFrame?.rewardShop);
    },
    panelStyle() {
      return `${this.skin?.header_desktop?.servicios || ''}${this.skin?.header_desktop?.nav_bar_text || ''}`;
    },
    profilePalette() {
      return getGamanzaProfilePalette(this.skin);
    },
    profileStyle() {
      return {
        '--gamanza-primary': this.profilePalette.primary,
        '--gamanza-primary-rgb': this.profilePalette.primaryRgb,
        '--gamanza-secondary': this.profilePalette.secondary,
        '--gamanza-contrast': this.profilePalette.contrastText,
        '--gamanza-surface': this.profilePalette.surface,
        '--gamanza-text': this.profilePalette.text
      };
    }
  },
  mounted() {
    document.addEventListener('click', this.closeFromOutside);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.closeFromOutside);
  },
  methods: {
    openPanel() {
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this.reloadExperienceStats();
      }
    },
    closePanel() {
      this.isOpen = false;
    },
    closeFromOutside(event) {
      if (this.isOpen && !this.$el.contains(event.target)) {
        this.closePanel();
      }
    },
    emitAndClose(eventName) {
      this.closePanel();
      this.$emit(eventName);
    },
    navigateAndClose(view) {
      this.closePanel();
      this.$emit('navigate-gamanza-page', view);
    },
    reloadExperienceStats() {
      this.$nextTick(() => {
        setTimeout(() => {
          this.experienceStatWidgets.forEach((stat) => gamanzaWidgetService.reloadOne(stat.id));
        }, 300);
      });
    },
    formatAmount(value) {
      const decimals = this.skin?.qBet_config?.decimalsQty || 2;
      return new Intl.NumberFormat('de-DE', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      }).format(Number(value) || 0);
    }
  }
};
</script>

<style scoped>
.gamanza-account-dropdown {
  position: relative;
  display: flex;
  align-items: center;
}

.gamanza-account-dropdown__panel {
  position: absolute;
  z-index: 1080;
  top: calc(100%);
  right: 0;
  width: 260px;
  padding: 16px 14px 13px;
  background: var(--gamanza-surface);
  border: 1px solid var(--gamanza-primary);
  border-radius: 0 0 6px 6px;
  box-shadow: 0 16px 30px rgba(var(--gamanza-primary-rgb), .35);
  color: var(--gamanza-text);
}

.gamanza-account-dropdown--mobile .gamanza-account-dropdown__panel {
  --gamanza-panel-x: -50%;
  position: fixed;
  top: 52px;
  left: 50%;
  right: auto;
  width: calc(100vw - 20px);
  max-width: 340px;
  max-height: calc(100vh - 70px);
  overflow-y: auto;
  transform: translateX(var(--gamanza-panel-x));
  border-radius: 8px;
}

.gamanza-dropdown-panel-enter-active {
  animation: gamanza-dropdown-panel-in 180ms ease-out;
}

@keyframes gamanza-dropdown-panel-in {
  from {
    opacity: 0;
    transform: translateX(var(--gamanza-panel-x, 0)) translateY(-6px) scale(.98);
  }

  to {
    opacity: 1;
    transform: translateX(var(--gamanza-panel-x, 0)) translateY(0) scale(1);
  }
}

.gamanza-account-dropdown__close {
  position: absolute;
  top: 6px;
  right: 8px;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
}

.gamanza-account-dropdown__identity {
  display: flex;
  flex-direction: column;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.25;
}

.gamanza-account-dropdown__balances {
  display: grid;
  gap: 3px;
  margin: 8px 0 12px;
  font-size: 10px;
  font-weight: 700;
}

.gamanza-account-dropdown__balances > div {
  display: flex;
  justify-content: space-between;
  gap: 10px;
}

.gamanza-account-dropdown__balances strong {
  color: #ffd029;
}

.gamanza-account-dropdown__account-actions,
.gamanza-account-dropdown__experience {
  display: grid;
  gap: 7px;
}

.gamanza-account-dropdown__account-actions {
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(var(--gamanza-primary-rgb), .45);
}

.gamanza-account-dropdown__logout {
  margin-top: 3px;
}

.gamanza-account-dropdown button:not(.gamanza-account-dropdown__close) {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 29px;
  gap: 9px;
  padding: 0 7px;
  border: 1px solid transparent;
  border-radius: 4px;
  background: transparent;
  color: inherit;
  font-size: 10px;
  font-weight: 700;
  text-align: left;
  cursor: pointer;
}

.gamanza-account-dropdown button:not(.gamanza-account-dropdown__close):hover {
  border-color: var(--gamanza-primary);
  background: rgba(var(--gamanza-primary-rgb), .12);
}

.gamanza-account-dropdown__account-actions i:first-child,
.gamanza-account-dropdown__experience button i:first-child {
  width: 18px;
  color: var(--gamanza-secondary);
  text-align: center;
}

.gamanza-account-dropdown__experience {
  margin-top: 10px;
}

.gamanza-account-dropdown__experience-title {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 10px;
  font-weight: 900;
}

.gamanza-account-dropdown__experience-stats {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 6px;
  padding: 8px;
  border: 1px solid rgba(var(--gamanza-primary-rgb), .45);
  border-radius: 4px;
  background: var(--gamanza-surface);
}

.gamanza-account-dropdown__experience-stat {
  min-width: 0;
  font-size: 9px;
  font-weight: 700;
  text-align: center;
}

.gamanza-account-dropdown__experience-stat--xp {
  grid-column: 1 / -1;
}

.gamanza-account-dropdown__experience-stat > span {
  display: block;
  margin-bottom: 2px;
  color: var(--gamanza-secondary);
  text-transform: uppercase;
}

.gamanza-account-dropdown__experience-stat .gamification_widget {
  min-height: 20px;
  overflow: hidden;
}

.gamanza-account-dropdown__experience-stat--rank .gamification_widget::v-deep * {
  line-height: 1.2;
  white-space: normal !important;
  word-break: normal;
}

.gamanza-account-dropdown__mark {
  display: grid;
  width: 21px;
  height: 21px;
  place-items: center;
  border-radius: 50%;
  border: 1px solid rgba(255, 226, 112, .85);
  background: radial-gradient(circle at 30% 24%, #fff8c9 0 11%, #ffd45a 34%, #c57a00 74%, #804300 100%);
  box-shadow: 0 2px 8px rgba(255, 190, 26, .45), inset 0 1px 1px rgba(255, 255, 255, .75);
  color: #713800;
  font-size: 11px;
}

.gamanza-account-dropdown__mark i {
  filter: drop-shadow(0 1px 0 rgba(255, 255, 255, .45));
}

.gamanza-account-dropdown__experience button i:last-child {
  margin-left: auto;
  color: var(--gamanza-secondary);
}
</style>
